import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import PaginatedList from '@theme/paginated-list';

/** Maximum number of skeleton placeholders shown after the loaded products (mirrors product-grid.liquid). */
const MAX_SKELETONS = 8;
const SKELETON_SELECTOR = '.product-grid__skeleton-item';

/**
 * Whether a grid item is a tip tile (a grid <li> that is neither a product card nor a skeleton).
 *
 * @param {Element | null} item
 * @returns {item is HTMLLIElement}
 */
const isTipTile = (item) =>
  item instanceof HTMLLIElement && !item.hasAttribute('ref') && !item.matches(SKELETON_SELECTOR);

/**
 * Identity of a tip tile: viewport variant + image, so the same tile matches even when the
 * `product-grid__item--{index}` class differs between two renders.
 *
 * @param {Element} item
 * @returns {string}
 */
const tipTileKey = (item) => {
  const variant = item.classList.contains('mobilegrid') ? 'mobile' : 'desktop';
  const image = item.querySelector('img')?.getAttribute('src') ?? item.textContent?.trim();

  return `${variant}|${image}`;
};

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {IntersectionObserver | undefined} */
  #skeletonObserver;

  /** The skeleton currently observed; used to avoid re-observing the same node. @type {Element | null} */
  #observedSkeleton = null;

  /** A clone of a server-rendered skeleton used to create the next batch. @type {Element | null} */
  #skeletonTemplate = null;

  /** Incremented whenever the filters change, so an in-flight response for the old state is dropped. */
  #loadToken = 0;

  /** Token of the in-flight request, or `null` when idle. @type {number | null} */
  #inFlightToken = null;

  /** Re-arms the skeleton observer whenever the grid is re-rendered (filters, sort, quick filter). */
  #gridMutationObserver = new MutationObserver(() => this.#observeFirstSkeleton());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.skeletonLoadingEnabled) {
      this.#observeFirstSkeleton();
      this.#gridMutationObserver.observe(this, { childList: true, subtree: true });
      document.addEventListener(ThemeEvents.FilterUpdate, this.#invalidatePendingLoad);
      document.addEventListener('filter-changed', this.#invalidatePendingLoad);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    this.#gridMutationObserver.disconnect();
    this.#skeletonObserver?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#invalidatePendingLoad);
    document.removeEventListener('filter-changed', this.#invalidatePendingLoad);
  }

  /**
   * Observes the first skeleton in the grid. Loading starts once it gets close to the viewport.
   */
  #observeFirstSkeleton = () => {
    // Query the live DOM rather than `refs`: the grid can be swapped via innerHTML (quick filter)
    // before the refs are re-synced, and observing a detached skeleton would stall pagination.
    const first = this.querySelector(`[ref="grid"] > ${SKELETON_SELECTOR}`);

    if (first === this.#observedSkeleton) return;

    this.#skeletonObserver?.disconnect();
    this.#observedSkeleton = first;

    if (!first) return;

    this.#skeletonTemplate ??= first.cloneNode(true);
    this.#skeletonObserver ??= new IntersectionObserver(this.#handleSkeletonIntersect, {
      rootMargin: '400px',
    });
    this.#skeletonObserver.observe(first);
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleSkeletonIntersect = (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) this.#loadNextPage();
  };

  #invalidatePendingLoad = () => {
    this.#loadToken++;
  };

  /**
   * Fetches the next page through the Section Rendering API and swaps the skeletons with its product cards.
   */
  async #loadNextPage() {
    const { grid } = this.refs;
    const nextPage = this.#getNextPage();

    // Skip when there is no next page or a request for the current filter state is already running
    if (!grid || !nextPage || this.#inFlightToken === this.#loadToken) return;

    const token = this.#loadToken;
    this.#inFlightToken = token;

    try {
      // Pass a copy: the renderer appends `section_id` to the URL it receives, and we push the clean one to history
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(nextPage.url));

      // Filters changed while fetching: the grid was re-rendered, drop this response
      if (token !== this.#loadToken || !this.isConnected) return;

      const skeletons = this.#getSkeletons(grid);
      const items = this.#withoutDuplicateTipTiles(grid, skeletons, this.#parseGridItems(html));

      this.#replaceSkeletons(grid, skeletons, items);
      this.#syncSkeletonCount(grid, nextPage.page);

      // `this.refs.cards` only updates once the component's own (requestIdleCallback-deferred)
      // mutation observer catches up, which can lag behind these edits. Without this, a skeleton
      // for the page after this one can already be in view (e.g. collections with no banner above
      // the grid) and trigger #loadNextPage() again before refs.cards reflects the cards we just
      // inserted, so #getNextPage() recomputes the SAME page number and re-renders it a second time.
      this.updatedCallback();

      history.pushState('', '', nextPage.url.toString());
    } catch (error) {
      console.error('Failed to load the next page of products', error);
    } finally {
      if (this.#inFlightToken === token) this.#inFlightToken = null;
      this.#observeFirstSkeleton();
    }
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    const { grid, cards } = this.refs;
    const lastCard = cards?.at(-1);

    if (!grid || !lastCard) return;

    const page = Number(lastCard.dataset.page) + 1;
    if (page > Number(grid.dataset.lastPage)) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * Extracts the grid items (product cards and tip tiles) from a rendered section, skipping its skeletons.
   *
   * @param {string} html
   * @returns {Element[]}
   */
  #parseGridItems(html) {
    const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');

    return grid ? Array.from(grid.querySelectorAll(`:scope > li:not(${SKELETON_SELECTOR})`)) : [];
  }

  /**
   * Drops the fetched page's leading tip tiles that Liquid already rendered at the end of the loaded grid.
   *
   * A tip tile positioned exactly on a page boundary (position == page * products_per_page) is rendered
   * twice by main-collection.liquid: after the last product of page N (`forloop.last` branch) and before
   * the first product of page N+1 (`total_index` branch). The server-rendered one is kept.
   *
   * @param {HTMLElement} grid
   * @param {Element[]} skeletons - The grid's current skeletons
   * @param {Element[]} items - The fetched page's grid items
   * @returns {Element[]}
   */
  #withoutDuplicateTipTiles(grid, skeletons, items) {
    const firstCardIndex = items.findIndex((item) => !isTipTile(item));
    const leadingCount = firstCardIndex === -1 ? items.length : firstCardIndex;

    if (leadingCount === 0) return items;

    // Keys of the tip tiles sitting right before the skeletons (i.e. after the last loaded product)
    const loadedKeys = new Set();
    let node = skeletons[0]?.previousElementSibling ?? grid.lastElementChild;

    while (isTipTile(node)) {
      loadedKeys.add(tipTileKey(node));
      node = node.previousElementSibling;
    }

    if (loadedKeys.size === 0) return items;

    const leadingTiles = items.slice(0, leadingCount).filter((tile) => !loadedKeys.has(tipTileKey(tile)));

    return leadingTiles.concat(items.slice(leadingCount));
  }

  /**
   * Swaps each fetched item into an existing skeleton so the grid does not jump.
   * Extra items (e.g. tip tiles) are inserted right after the previous item.
   *
   * @param {HTMLElement} grid
   * @param {Element[]} skeletons - The grid's current skeletons
   * @param {Element[]} items - The fetched page's grid items
   */
  #replaceSkeletons(grid, skeletons, items) {
    /** @type {Element | null} */
    let previous = null;

    items.forEach((item, index) => {
      const skeleton = skeletons[index];

      if (skeleton) {
        skeleton.replaceWith(item);
      } else if (previous) {
        previous.after(item);
      } else {
        grid.append(item);
      }

      previous = item;
    });
  }

  /**
   * Adds or removes skeletons so their number matches the products still left to load (max 8).
   *
   * @param {HTMLElement} grid
   * @param {number} loadedPage - The last page that has been rendered
   */
  #syncSkeletonCount(grid, loadedPage) {
    const { productsCount, productsPerPage } = grid.dataset;
    const remaining = Number(productsCount) - loadedPage * Number(productsPerPage);
    const needed = Math.max(0, Math.min(MAX_SKELETONS, remaining));
    const skeletons = this.#getSkeletons(grid);

    if (skeletons.length > needed) {
      skeletons.slice(needed).forEach((skeleton) => skeleton.remove());
    } else if (skeletons.length < needed && this.#skeletonTemplate) {
      const fragment = document.createDocumentFragment();

      for (let i = skeletons.length; i < needed; i++) {
        fragment.append(this.#skeletonTemplate.cloneNode(true));
      }

      grid.append(fragment);
    }
  }

  /**
   * @param {HTMLElement} grid
   * @returns {Element[]}
   */
  #getSkeletons(grid) {
    return Array.from(grid.querySelectorAll(`:scope > ${SKELETON_SELECTOR}`));
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
